import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import IPTVService from '../../../services/IPTVService';
import IPTVChannelList from './IPTVChannelList';
import IPTVChannelForm from './IPTVChannelForm';

const IPTVManager = () => {
  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [channelsRes, categoriesRes, statusRes] = await Promise.all([
        IPTVService.getChannels(),
        IPTVService.getCategories(),
        IPTVService.getStatus()
      ]);

      setChannels(channelsRes.data || []);
      setCategories(categoriesRes.data || []);
      setStatus(statusRes.data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load IPTV data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChannel = async (channelData) => {
    try {
      await IPTVService.createChannel(channelData);
      setShowForm(false);
      fetchData();
    } catch (err) {
      alert(err.message || 'Failed to create channel');
    }
  };

  const handleUpdateChannel = async (channelData) => {
    try {
      await IPTVService.updateChannel(editingChannel.id, channelData);
      setEditingChannel(null);
      setShowForm(false);
      fetchData();
    } catch (err) {
      alert(err.message || 'Failed to update channel');
    }
  };

  const handleDeleteChannel = async (id) => {
    if (!window.confirm('Are you sure you want to delete this channel?')) {
      return;
    }

    try {
      await IPTVService.deleteChannel(id);
      fetchData();
    } catch (err) {
      alert(err.message || 'Failed to delete channel');
    }
  };

  const handleEditChannel = (channel) => {
    setEditingChannel(channel);
    setShowForm(true);
  };

  const handleDownloadPlaylist = async () => {
    try {
      await IPTVService.downloadPlaylist();
    } catch (err) {
      alert(err.message || 'Failed to download playlist');
    }
  };

  const handleReorder = async (newOrder) => {
    try {
      const orders = newOrder.map((channel, index) => ({
        id: channel.id,
        sort_order: index
      }));
      await IPTVService.reorderChannels(orders);
      fetchData();
    } catch (err) {
      alert(err.message || 'Failed to reorder channels');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Check if IPTV is enabled for this user
  if (status && !status.enabled) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fas fa-satellite-dish fa-3x text-muted mb-3"></i>
                <h4>IPTV Module Not Available</h4>
                <p className="text-muted">
                  {status.reason === 'NO_SUBSCRIPTION' 
                    ? 'You need an active subscription to access IPTV features.'
                    : 'Please upgrade to a plan that includes IPTV features.'}
                </p>
                <Link to="/pricing" className="btn btn-primary">
                  View Pricing Plans
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h4 className="card-title">IPTV Channel Management</h4>
              <p className="text-muted mb-0">
                Manage your IPTV channels and generate M3U playlists
                {status && (
                  <span className="ms-2 badge bg-info">
                    {status.channel_count} / {status.max_channels} channels
                  </span>
                )}
              </p>
            </div>
            <div className="d-flex gap-2">
              <button 
                className="btn btn-outline-primary"
                onClick={handleDownloadPlaylist}
              >
                <i className="fas fa-download me-2"></i>
                Download M3U
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setEditingChannel(null);
                  setShowForm(true);
                }}
                disabled={status && status.channel_count >= status.max_channels}
              >
                <i className="fas fa-plus me-2"></i>
                Add Channel
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {showForm && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">
                  {editingChannel ? 'Edit Channel' : 'Add New Channel'}
                </h5>
              </div>
              <div className="card-body">
                <IPTVChannelForm
                  initialData={editingChannel}
                  categories={categories}
                  onSubmit={editingChannel ? handleUpdateChannel : handleCreateChannel}
                  onCancel={() => {
                    setShowForm(false);
                    setEditingChannel(null);
                  }}
                />
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-body">
              <IPTVChannelList
                channels={channels}
                categories={categories}
                onEdit={handleEditChannel}
                onDelete={handleDeleteChannel}
                onReorder={handleReorder}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IPTVManager;
